import { ApiResponse } from 'apisauce';

import config from '../../config';
import { PredefinedMilisecondsTypeEnum } from '../enums';
import {
  AccountsSummaryRequest,
  AutoPassRequest,
  AutoPassResponse,
  CouponPaymentParams,
  CouponPreviewParams,
  CouponPreviewResponse,
  CreatePlanPaymentParams,
  CreatePlanPaymentResponse,
  CreatePurchasePaymentParam,
  ModifyOwnBirthdateRequest,
  AccountSummary,
  OwnerTradingAccount,
  Payment,
  PaymentsFilter,
  SearchTradingPlansRequest,
  TradingPlan,
  WithdrawalRequest,
  CreateReferralPayoutRequest,
  ReferralCommissionEntry,
  ReferralPagedRequest,
  ReferralPagedResponse,
  ReferralPayoutRequest,
  ReferralProfileResponse,
  ReferralQrCode,
  ReferralReferredUser,
} from '../models/crm/types';
import {
  CountryData,
} from '../models/enroll';
import { CryptoLinkedAccountRequestData, LinkedAccount, LinkedAccountRequestData } from '../models/linked-account/types';
import { applyPathParams } from '../util/DataHelpers';
import { parseToMultipartData } from '../util/RequestHelpers';

import { CacheName, HttpMethods } from './cache/types';
import { BaseRequestService } from './BaseRequest.service';
import { ICRMService } from './types';

/**
 * Path params marked given as $1, $2 etc.
 */
const API_PATH = {
  accountsSummary: '/api/v1.1/report/accounts-summary',

  // internal transfer

  // individual
  activateDemo: '/api/v1/individual/$1/activate-demo',

  // enroll
  getIndividualExtendedInfo: '/api/v1/individual/extended-info',
  postEnrollmentOwnPersonal: '/api/v1.1/enrollment/own/personal',
  postIndividualAddressOwn: '/api/v1/individual-address/own',
  putIndividualOwnBirthdate: '/api/v1/individual/own/birthdate',

  // transitions
  putIndividualTransitionFromNewToForVerification: '/api/v1/individual/$1/transition-from-new-to-for-verification',
  getPaymentsAccountsIndividual: '/api/v1/report/payments-accounts-individual',


  // ACE trading-plans
  postSearchTradingPlans: '/api/v1/trading-plan/search',

  // ACE Autopass (enabled/disabled)
  clientSettingsTradingPlan: '/api/v1/trading-plan/client-settings',

  // Linked Accounts
  getLinkedAccountsByIndividualId: '/api/v1/LinkedAccount/by-individual/',
  registerLinkedAccount: '/api/v1/LinkedAccount',

  // Payments
  getOwnerTradingAccounts: '/api/v1/account-structure/by-owner/$1/1',
  registerWithdrawalRequest: '/api/v1/Payment/withdrawal',
  purchaseTradingPlan: '/api/v1/Payment/purchase/plan',
  createPurchase: '/api/v1/Payment/purchase/create',
  couponPreview: '/api/v1/coupons/preview',

  // Referrals
  referralOwnProfile: '/api/v1/referral/own/profile',
  referralOwnQr: '/api/v1/referral/own/qr',
  referralOwnReferredUsers: '/api/v1/referral/own/referred-users',
  referralOwnCommissions: '/api/v1/referral/own/commissions',
  referralOwnPayoutRequest: '/api/v1/referral/own/payout-request',

  //  Open endpoints - publicly accessible and do not require a token.
  getCountryList: '/api/v1/country/list',
  getClientPlatforms: '/api/v1/TradingPlatform/get-client-platforms',
};

export class CRMService extends BaseRequestService implements ICRMService {
  protected getBaseUrl(): string {
    return config.crm.url;
  }

  constructor() {
    super();
    this.enrollGet = this.enrollGet.bind(this);
    this.enrollPost = this.enrollPost.bind(this);
    this.getPayments = this.getPayments.bind(this);
    this.getLinkedAccountsByIndividualId = this.getLinkedAccountsByIndividualId.bind(this);
    this.registerLinkedAccount = this.registerLinkedAccount.bind(this);
    this.registerCryptoLinkedAccount = this.registerCryptoLinkedAccount.bind(this);
    this.getOwnerTradingAccounts = this.getOwnerTradingAccounts.bind(this);
    this.registerWithdrawalRequest = this.registerWithdrawalRequest.bind(this);
    this.modifyOwnBirthdate = this.modifyOwnBirthdate.bind(this);
  }


  public getAccountsSummary(individualData: AccountsSummaryRequest) {
    return this.api.get<AccountSummary[]>(API_PATH.accountsSummary, individualData);
  }

  public searchTradingPlans(request: SearchTradingPlansRequest) {
    return this.api.post<TradingPlan[]>(API_PATH.postSearchTradingPlans, request);
  }

  /** 404 means the user has no referral profile yet. */
  public getReferralOwnProfile() {
    return this.api.get<ReferralProfileResponse>(API_PATH.referralOwnProfile);
  }

  /** 404 when the QR is disabled or the referrer cannot invite. */
  public getReferralOwnQr() {
    return this.api.get<ReferralQrCode>(API_PATH.referralOwnQr);
  }

  public getReferralOwnReferredUsers(request?: ReferralPagedRequest) {
    return this.api.get<ReferralPagedResponse<ReferralReferredUser>>(API_PATH.referralOwnReferredUsers, request);
  }

  public getReferralOwnCommissions(request?: ReferralPagedRequest) {
    return this.api.get<ReferralPagedResponse<ReferralCommissionEntry>>(API_PATH.referralOwnCommissions, request);
  }

  public createReferralOwnPayoutRequest(request: CreateReferralPayoutRequest) {
    return this.api.post<ReferralPayoutRequest>(API_PATH.referralOwnPayoutRequest, request);
  }

  public getAutoPass() {
    return this.api.get<AutoPassResponse>(API_PATH.clientSettingsTradingPlan);
  }

  public updateAutoPass(request: AutoPassRequest) {
    return this.api.put<AutoPassResponse>(API_PATH.clientSettingsTradingPlan, request);
  }

  public getCountryList() {
    const APIPath = API_PATH.getCountryList;
    const url = this.getBaseUrl() + APIPath;
    this.addOrUpdateCacheDefinition(url, CacheName.countries, HttpMethods.GET, PredefinedMilisecondsTypeEnum.oneHour);
    return this.api.get<CountryData[]>(APIPath);
  }

  public getTradingPlatforms() {
    const requestParams = {
      portalId: 'ACEPortal',
      useForEnrolment: true,
      tenantId: config.auth.tenantId,
    };

    const APIPath = API_PATH.getClientPlatforms;
    const url = this.getBaseUrl() + APIPath;
    this.addOrUpdateCacheDefinition(
      url,
      CacheName.clientPlatforms,
      HttpMethods.GET,
      PredefinedMilisecondsTypeEnum.oneHour,
      [ 'portalId', 'useForEnrolment', 'tenantId' ],
    );

    return this.api.get<any>(APIPath, requestParams, { timeout: 3000 });
  }

  /**
   * Makes a GET enroll request with given call name and path parameters (the last is optional)
   * @param callName For example getIndividualExtendedInfo - see ENROLL_METHODS in library models
   * @param pathParams Any path parameters - in API_PATH params are marked with $N - for example $1, $2 etc.
   */
  public async enrollGet(callName: string, pathParams?: string[], _token?: string) {
    let path = (API_PATH as any)[callName];
    if (!path) {
      throw new Error(`[CRM::enrollGet] No such path - path='${path}', key='${callName}'`);
    }

    const resultPath: string = applyPathParams(path, pathParams);

    return this.api.get<any>(resultPath);
  }

  public async enrollPost(callName: string, requestBody: any) {
    let path = (API_PATH as any)[callName];
    if (!path) {
      throw new Error(`[CRM::enrollPost] No such path - path='${path}', key='${callName}'`);
    }

    return this.api.post<any>(path, requestBody);
  }

  // eslint-disable-next-line max-len

  public async registerLinkedAccount(rawData: LinkedAccountRequestData) {
    const requestBody = parseToMultipartData('bank-account', rawData, []);
    return this.api.post(API_PATH.registerLinkedAccount, requestBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  public async registerCryptoLinkedAccount(rawData: CryptoLinkedAccountRequestData) {
    const requestBody = parseToMultipartData('crypto-account', rawData, []);
    return this.api.post(API_PATH.registerLinkedAccount, requestBody, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  createPlanPayment(data: CreatePlanPaymentParams): Promise<ApiResponse<CreatePlanPaymentResponse>> {
    return this.postCouponBackedPayment(API_PATH.purchaseTradingPlan, data);
  }

  createPurchasePayment(data: CreatePurchasePaymentParam): Promise<ApiResponse<CreatePlanPaymentResponse>> {
    return this.postCouponBackedPayment(API_PATH.createPurchase, data);
  }

  private postCouponBackedPayment<T extends CouponPaymentParams>(
    APIPath: string,
    data: T,
  ): Promise<ApiResponse<CreatePlanPaymentResponse>> {
    const { idempotency_key, ...body } = data;
    if (idempotency_key) {
      return this.api.post(APIPath, body, {
        headers: { 'Idempotency-Key': idempotency_key },
      });
    }
    return this.api.post(APIPath, data);
  }

  couponPreview(data: CouponPreviewParams): Promise<ApiResponse<CouponPreviewResponse>> {
    return this.api.post(API_PATH.couponPreview, data);
  }

  public getPayments(filter: PaymentsFilter): Promise<ApiResponse<Payment[]>> {
    return this.api.post<Payment[]>(API_PATH.getPaymentsAccountsIndividual, filter);
  }

  public activateDemoAccount(individualId: number): Promise<ApiResponse<void>> {
    const APIPath = API_PATH.activateDemo.replace('$1', String(individualId));
    return this.api.put(APIPath);
  }

  public updateIndividualTransitionFromNewToForVerification(
    individualId: number,
  ): Promise<ApiResponse<void>> {
    const APIPath = API_PATH.putIndividualTransitionFromNewToForVerification.replace('$1', String(individualId));
    return this.api.put(APIPath);
  }

  public modifyOwnBirthdate(
    request: ModifyOwnBirthdateRequest,
  ): Promise<ApiResponse<void>> {
    return this.api.put(API_PATH.putIndividualOwnBirthdate, request);
  }

  public async getLinkedAccountsByIndividualId(individualId: number) {
    return this.api.get<LinkedAccount[]>(API_PATH.getLinkedAccountsByIndividualId + individualId);
  }







  public async getOwnerTradingAccounts(individualId: number): Promise<ApiResponse<OwnerTradingAccount[]>> {
    return this.api.get<OwnerTradingAccount[]>(API_PATH.getOwnerTradingAccounts.replace('$1', individualId.toString()));
  }

  public registerWithdrawalRequest(withdrawalRequest: WithdrawalRequest) {
    return this.api.post<any>(API_PATH.registerWithdrawalRequest, withdrawalRequest);
  }
}
